class VmMoments extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.rail = this.querySelector('.vm-moments__rail');
    this.rail?.querySelectorAll('[data-vm-loop-copy]').forEach(card => card.remove());
    this.cards = [...this.querySelectorAll('.vm-moment')];
    if (!this.rail || !this.cards.length) return;
    this.slideCount = this.cards.length;
    if (this.slideCount > 1) {
      const copy = () => this.cards.map(card => {
        const clone = card.cloneNode(true);
        clone.dataset.vmLoopCopy = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('button,video').forEach(control => control.setAttribute('tabindex', '-1'));
        return clone;
      });
      this.rail.prepend(...copy());
      this.rail.append(...copy());
      this.cards = [...this.rail.querySelectorAll('.vm-moment')];
    }
    this.current = this.slideCount > 1 ? this.slideCount : 0;
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.previous = this.querySelector('[data-vm-moment-prev]');
    this.next = this.querySelector('[data-vm-moment-next]');
    this.cards.forEach((card, index) => {
      const video = card.querySelector('video');
      const button = card.querySelector('.vm-moment__toggle');
      if (!video || !button) return;
      video.controls = false;
      video.muted = true;
      button.hidden = false;
      const updateButton = () => {
        button.setAttribute('aria-pressed', String(!video.paused));
        button.querySelector('span').textContent = video.paused ? '▶' : 'Ⅱ';
      };
      video.addEventListener('play', updateButton, options);
      video.addEventListener('pause', updateButton, options);
      button.addEventListener('click', () => {
        const shouldPlay = index !== this.current || video.paused;
        if (index !== this.current) this.center(index);
        if (shouldPlay) {
          this.manuallyPaused = false;
          video.play().catch(() => {});
        } else {
          this.manuallyPaused = true;
          video.pause();
        }
      }, options);
    });
    this.previous?.addEventListener('click', () => this.center(this.current - 1), options);
    this.next?.addEventListener('click', () => this.center(this.current + 1), options);
    this.rail.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.center(this.current + (event.key === 'ArrowRight' ? 1 : -1));
    }, options);
    this.rail.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        const bounds = this.rail.getBoundingClientRect();
        const center = bounds.left + bounds.width / 2;
        const distances = this.cards.map(card => {
          const rect = card.getBoundingClientRect();
          return Math.abs(rect.left + rect.width / 2 - center);
        });
        this.current = distances.indexOf(Math.min(...distances));
        if (this.slideCount > 1 && (this.current < this.slideCount || this.current >= this.slideCount * 2)) {
          this.center(this.slideCount + this.current % this.slideCount, false);
          return;
        }
        this.update();
      }, 100);
    }, { ...options, passive: true });
    this.observer = new IntersectionObserver(entries => {
      this.inView = entries[0].isIntersecting;
      this.update();
    }, { threshold: 0.35 });
    this.observer.observe(this.rail);
    this.previewObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const video = entry.target.querySelector('video');
        if (video && video.readyState === 0) {
          video.preload = 'metadata';
          video.load();
        }
        this.previewObserver.unobserve(entry.target);
      });
    }, { rootMargin: '200px', threshold: 0.01 });
    this.cards.forEach(card => {
      const video = card.querySelector('video');
      video?.addEventListener('loadedmetadata', () => {
        if (video.paused && video.currentTime === 0) video.currentTime = 0.01;
      }, { ...options, once: true });
      video?.addEventListener('loadeddata', () => this.update(), { ...options, once: true });
      this.previewObserver.observe(card);
    });
    this.resizeObserver = new ResizeObserver(() => this.center(this.current, false));
    this.resizeObserver.observe(this.rail);
    this.center(this.current, false);
  }
  center(index, animate = true) {
    this.current = (index + this.cards.length) % this.cards.length;
    const card = this.cards[this.current];
    const railBounds = this.rail.getBoundingClientRect();
    const cardBounds = card.getBoundingClientRect();
    this.rail.scrollBy({ left: cardBounds.left + cardBounds.width / 2 - railBounds.left - railBounds.width / 2, behavior: animate && !this.reducedMotion ? 'smooth' : 'instant' });
    this.update();
  }
  update() {
    this.cards.forEach((card, index) => {
      card.classList.toggle('is-current', index === this.current);
      const video = card.querySelector('video');
      if (!video) return;
      if (index === this.current && this.inView && !this.reducedMotion && !this.manuallyPaused) video.play().catch(() => {});
      else video.pause();
    });
    if (this.previous) this.previous.disabled = this.slideCount < 2;
    if (this.next) this.next.disabled = this.slideCount < 2;
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.previewObserver?.disconnect();
    this.resizeObserver?.disconnect();
    clearTimeout(this.scrollTimer);
    this.querySelectorAll('video').forEach(video => video.pause());
  }
}
if (!customElements.get('vm-moments')) customElements.define('vm-moments', VmMoments);
